import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AppConstants } from 'src/app/shared/constants/app.contants';
import { ApiService } from 'src/app/shared/services/api.service';
import { LoaderService } from 'src/app/shared/services/loader.service';
import { ToasterService } from 'src/app/shared/services/toaster.service';
import { environment } from 'src/environments/environment';
import { TableColumn } from 'src/app/shared/components/data-table/data-table.component';

@Component({
  selector: 'app-approved-prs',
  templateUrl: './approved-prs.component.html',
  styleUrls: ['./approved-prs.component.scss']
})
export class ApprovedPRsComponent implements OnInit {
  private readonly tcAutoTenderThreshold = 200000;
  AppConstants = AppConstants;
  approvedPRs: any[] = [];
  searchTerm: string = '';
  filteredPRs: any[] = [];
  tableColumns: TableColumn[] = [];

  constructor(
    private _loaderService: LoaderService,
    private apiService: ApiService,
    private toasterService: ToasterService,
    private _router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.initializeTableColumns();
    this.fetchApprovedPRs();
  }

  private initializeTableColumns(): void {
    this.tableColumns = [
      {
        field: 'prNumber',
        header: 'PR Number',
        type: 'link'
      },
      // {
      //   field: 'department',
      //   header: 'Department',
      //   type: 'text'
      // },
      {
        field: 'requisitionPurpose',
        header: 'Requisition Purpose',
        type: 'text'
      },
      {
        field: 'prName',
        header: 'PR Name',
        type: 'text'
      },
      {
        field: 'value',
        header: 'Value (QAR)',
        type: 'number',
        format: '1.2-2'
      },
      {
        field: 'approvedDate',
        header: 'Approved Date',
        type: 'date',
        format: 'dd/MM/yyyy'
      },
      {
        field: 'status',
        header: 'Status',
        type: 'badge',
        badgeClass: 'status-badge'
      },
      {
        field: 'isTC',
        header: 'Is Tender PR',
        type: 'badge',
        badgeClass: 'status-badge'
      },
      {
        field: 'actions',
        header: 'Actions',
        type: 'action',
        actions: [
          {
            action: 'toggleTC',
            title: 'IsTC',
            type: 'checkbox',
            checkedField: 'isTC',
            preventToggleWhen: (row: any) => this.isTCFlagLocked(row)
          },
          {
            iconPath: 'assets/icons/eye-icon.svg',
            action: 'view',
            title: 'View'
          }
        ]
      }
    ];
  }

  async fetchApprovedPRs(): Promise<void> {
    this._loaderService.showLoader();
    
    try {
      this.apiService.setBaseUrl(environment.API_URL);
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.PurchaseRequisition}/${AppConstants.ACTIONS.GetAllApprovedPR}`;
      const response: any = await lastValueFrom(
        this.apiService.getByUrl(url)
      );

      // Check if request was successful
      if (!response?.isSuccess) {
        const errorMsg = response?.errorMessageEn || response?.errorMessageAr || 'Failed to fetch approved PRs';
        console.error(`[Approved PRs] API Error: ${errorMsg}`);
        this.clearPRsData();
        this.cdr.detectChanges();
        return;
      }

      // Extract PRs data from response
      const prsData = this.extractPRsData(response);

      if (prsData && prsData.length > 0) {
        this.approvedPRs = prsData.map(pr => this.mapToPRData(pr));
        this.filteredPRs = [...this.approvedPRs];
      } else {
        this.clearPRsData();
      }
      
      this.refreshPRRows();
    } catch (error: any) {
      const errorMessage = error?.message || 'Unknown error occurred';
      console.error(`[Approved PRs] Exception while fetching approved PRs:`, errorMessage, error);
      this.clearPRsData();
      this.refreshPRRows();
    } finally {
      this._loaderService.hideLoader();
    }
  }

  private extractPRsData(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }
    
    // Check if response has data property
    if (response?.data) {
      if (Array.isArray(response.data)) {
        return response.data;
      }
      if (typeof response.data === 'object') {
        return response.data.data || response.data.result || [];
      }
    }
    
    // Check if response has result property
    if (Array.isArray(response?.result)) {
      return response.result;
    }
    
    if (response?.isSuccess && (response?.data || response?.result)) {
      return response.data || response.result || [];
    }
    
    return [];
  }

  private mapToPRData(pr: any): any {
    return {
      prNumber: pr.prNumber || pr.PRNumber || pr.pr_Number || '',
      department: pr.department || pr.Department || '',
      prName: pr.prName || pr.PRName || '',
      requisitionPurpose: pr.requisitionPurpose || pr.RequisitionPurpose || '',
      description: pr.description || pr.Description || '',
      value: this.parsePRValue(pr.value || pr.Value || pr.amount || pr.Amount || 0),
      approvedDate: this.parseDate(pr.approvedDate || pr.ApprovedDate || pr.approved_Date),
      status: pr.status || pr.Status || '',
      linkedTender: pr.linkedTender || pr.LinkedTender || pr.linked_Tender || null,
      isTC: this.parsePRValue(pr.value || pr.Value || pr.amount || pr.Amount || 0) >= this.tcAutoTenderThreshold ? true : this.normalizeBoolean(pr.isTC ?? pr.IsTC ?? pr.icTC ?? pr.IcTC ?? false)
    };
  }

  private clearPRsData(): void {
    this.approvedPRs = [];
    this.filteredPRs = [];
  }

  private parseDate(dateValue: any): Date | null {
    if (!dateValue) {
      return null;
    }
    if (dateValue instanceof Date) {
      return dateValue;
    }
    try {
      const parsedDate = new Date(dateValue);
      return isNaN(parsedDate.getTime()) ? null : parsedDate;
    } catch {
      return null;
    }
  }

  private parsePRValue(value: any): number {
    if (typeof value === 'number') {
      return value;
    }

    const numericValue = Number((value ?? '').toString().replace(/[^0-9.-]/g, ''));
    return Number.isFinite(numericValue) ? numericValue : 0;
  }

  private normalizeBoolean(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }

    const normalizedValue = (value ?? '').toString().trim().toLowerCase();
    return normalizedValue === 'true' || normalizedValue === '1' || normalizedValue === 'yes';
  }

  private isTCFlagLocked(row: any): boolean {
    return this.parsePRValue(row?.value) >= this.tcAutoTenderThreshold;
  }

  private refreshPRRows(): void {
    this.filteredPRs = [...this.filteredPRs];
    this.approvedPRs = [...this.approvedPRs];
    this.cdr.detectChanges();
  }
  onSearch(): void {
    if (!this.searchTerm.trim()) {
      this.filteredPRs = [...this.approvedPRs];
    } else {
      const searchLower = this.searchTerm.toLowerCase();
      this.filteredPRs = this.approvedPRs.filter(pr =>
        Object.values(pr).some(val =>
          typeof val === 'string' && val.toLowerCase().includes(searchLower)
        ) || 
        (typeof pr.value === 'number' && pr.value.toString().includes(this.searchTerm))
      );
    }
    this.cdr.detectChanges();
  }

  exportData(): void {
    // TODO: Implement export functionality
  }

  viewPR(prNumber: string): void {
    this._router.navigate(['/', AppConstants.ROUTES.APPROVED_PRS, AppConstants.ROUTES.PRDETAILS, prNumber]);
  }

  confirmPR(prNumber: string): void {
    // TODO: Implement confirm functionality
  }

  navigateToTender(tenderId: string): void {
    this._router.navigate([`/${AppConstants.ROUTES.TENDER}/${tenderId}`]);
  }

  async onTableActionClick(event: {action: string, row: any, checked?: boolean}): Promise<void> {
    if (event.action === 'view') {
      this.viewPR(event.row.prNumber);
      return;
    }

    if (event.action === 'toggleTC') {
      await this.updateTCFlag(event.row, !!event.checked);
    }
  }

  private async updateTCFlag(row: any, isTC: boolean): Promise<void> {
    if (!row?.prNumber) {
      return;
    }

    const previousValue = !!row.isTC;

    if (this.isTCFlagLocked(row)) {
      row.isTC = true;
      this.refreshPRRows();
      this.toasterService.showError('PRs with value equal to or above 200,000 QAR must remain TC required.', 'TC cannot be changed');
      return;
    }

    row.isTC = isTC;
    this.refreshPRRows();

    try {
      const url = `${environment.API_URL}/api/${AppConstants.CONTROLLERS.PurchaseRequisition}/${AppConstants.ACTIONS.UpdateTCFlag}`;
      const response: any = await lastValueFrom(
        this.apiService.putByUrl(url, {
          prNumber: row.prNumber,
          isTC
        })
      );

      if (response?.isSuccess || response?.IsSuccess) {
        this.toasterService.showSuccess('TC flag updated successfully', 'Success');
      } else {
        row.isTC = previousValue;
        this.refreshPRRows();
        const message = response?.errorMessageEn || response?.ErrorMessageEn || 'Failed to update TC flag';
        this.toasterService.showError(message, 'Error');
      }
    } catch (error: any) {
      row.isTC = previousValue;
      this.refreshPRRows();
      this.toasterService.showError(error?.message || 'Failed to update TC flag', 'Error');
    } finally {
      this.refreshPRRows();
    }
  }

  onTableLinkClick(event: {field: string, row: any}): void {
    if (event.field === 'prNumber') {
      this.viewPR(event.row.prNumber);
    } else if (event.field === 'linkedTender') {
      this.navigateToTender(event.row.linkedTender);
    }
  }
}

